import { z } from 'zod'

export const registrationQuestionTypeValues = [
  'short_text',
  'long_text',
  'single_choice',
  'multiple_choice',
  'checkbox',
  'number',
] as const
export type RegistrationQuestionType = (typeof registrationQuestionTypeValues)[number]

export const MAX_REGISTRATION_QUESTIONS = 10
export const MAX_REGISTRATION_OPTIONS = 20
export const REGISTRATION_SHORT_TEXT_MAX = 200
export const REGISTRATION_LONG_TEXT_MAX = 2000

/** The version of the sign-up consent text a web guest agrees to. */
export const REGISTRATION_CONSENT_VERSION = '2026-10-27'

const choiceTypes: ReadonlySet<RegistrationQuestionType> = new Set([
  'single_choice',
  'multiple_choice',
])

export const registrationQuestionSchema = z
  .object({
    id: z
      .string()
      .trim()
      .min(1)
      .max(40)
      .regex(/^[A-Za-z0-9_-]+$/),
    type: z.enum(registrationQuestionTypeValues),
    label: z.string().trim().min(1).max(200),
    required: z.boolean().default(false),
    options: z.array(z.string().trim().min(1).max(100)).max(MAX_REGISTRATION_OPTIONS).optional(),
  })
  .superRefine((question, ctx) => {
    if (!choiceTypes.has(question.type)) return
    const options = question.options ?? []
    if (options.length < 2) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['options'],
        message: 'A choice question needs at least two options',
      })
    }
    if (new Set(options).size !== options.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['options'],
        message: 'Options must be different',
      })
    }
  })
export type RegistrationQuestion = z.infer<typeof registrationQuestionSchema>

/** The sign-up form an organizer sets on an event. */
export const eventRegistrationConfigSchema = z
  .object({
    enabled: z.boolean(),
    questions: z.array(registrationQuestionSchema).max(MAX_REGISTRATION_QUESTIONS).default([]),
    /** Seats to fill; null for no limit. */
    seatLimit: z.number().int().positive().max(100_000).nullable().default(null),
    /** Once the seats are taken, further sign-ups go on a waiting list instead of being refused. */
    waitlist: z.boolean().default(true),
    /** Sign-ups close at this moment; null keeps them open until the event ends. */
    deadline: z.iso.datetime().nullable().default(null),
  })
  .superRefine((config, ctx) => {
    const ids = config.questions.map((question) => question.id)
    if (new Set(ids).size !== ids.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['questions'],
        message: 'Question ids must be unique',
      })
    }
  })
export type EventRegistrationConfig = z.infer<typeof eventRegistrationConfigSchema>

export const registrationStateValues = ['open', 'waitlist', 'full', 'closed'] as const
export type RegistrationState = (typeof registrationStateValues)[number]

export const registrationStatusValues = ['confirmed', 'waitlist', 'cancelled'] as const
export type RegistrationStatus = (typeof registrationStatusValues)[number]

/** The sign-up summary on the event detail; `myStatus` only on viewer-specific responses. */
export const eventRegistrationSummarySchema = z.object({
  enabled: z.boolean(),
  state: z.enum(registrationStateValues),
  myStatus: z.enum(registrationStatusValues).nullable().optional(),
})
export type EventRegistrationSummary = z.infer<typeof eventRegistrationSummarySchema>

export const registrationAnswerValueSchema = z.union([
  z.string().max(REGISTRATION_LONG_TEXT_MAX),
  z.array(z.string().max(100)).max(MAX_REGISTRATION_OPTIONS),
  z.boolean(),
  z.number(),
])
export type RegistrationAnswerValue = z.infer<typeof registrationAnswerValueSchema>
export type RegistrationAnswers = Record<string, RegistrationAnswerValue>

const answersSchema = z.record(z.string().max(40), registrationAnswerValueSchema)

export const myRegistrationSchema = z.object({
  id: z.string().uuid(),
  status: z.enum(registrationStatusValues),
  name: z.string(),
  email: z.string(),
  answers: answersSchema,
  /** Place on the waiting list, 1 for next in line; null when not waiting. */
  waitlistPosition: z.number().int().nullable(),
  createdAt: z.string(),
})
export type MyRegistration = z.infer<typeof myRegistrationSchema>

/** GET /api/events/{eventId}/registration: the form, its state and the viewer's sign-up. */
export const eventRegistrationFormResponseSchema = z.object({
  eventId: z.string().uuid(),
  enabled: z.boolean(),
  state: z.enum(registrationStateValues),
  questions: z.array(registrationQuestionSchema),
  seatLimit: z.number().int().nullable(),
  seatsLeft: z.number().int().nullable(),
  deadline: z.string().nullable(),
  membersOnly: z.boolean(),
  minAge: z.number().int().nullable(),
  consentVersion: z.string(),
  mine: myRegistrationSchema.nullable(),
})
export type EventRegistrationForm = z.infer<typeof eventRegistrationFormResponseSchema>

/** POST /api/events/{eventId}/registration */
export const registerForEventRequestSchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  email: z.string().trim().toLowerCase().email().max(254).optional(),
  answers: answersSchema.default({}),
  consent: z.boolean().optional(),
  locale: z.enum(['de', 'en']).optional(),
})
export type RegisterForEventRequest = z.input<typeof registerForEventRequestSchema>

export const registerForEventResponseSchema = z.object({ registration: myRegistrationSchema })

/** One sign-up as the organizer sees it, with the private answers. */
export const eventRegistrationSchema = z.object({
  id: z.string().uuid(),
  status: z.enum(registrationStatusValues),
  name: z.string(),
  email: z.string(),
  userProfileId: z.string().uuid().nullable(),
  answers: answersSchema,
  consentAt: z.string().nullable(),
  createdAt: z.string(),
  cancelledAt: z.string().nullable(),
})
export type EventRegistration = z.infer<typeof eventRegistrationSchema>

export const registrationCountsSchema = z.object({
  confirmed: z.number().int(),
  waitlist: z.number().int(),
  cancelled: z.number().int(),
})
export type RegistrationCounts = z.infer<typeof registrationCountsSchema>

/** GET/PUT /api/businesses/{businessId}/events/{eventId}/registration */
export const eventRegistrationSettingsResponseSchema = z.object({
  config: eventRegistrationConfigSchema,
  state: z.enum(registrationStateValues),
  counts: registrationCountsSchema,
})
export type EventRegistrationSettings = z.infer<typeof eventRegistrationSettingsResponseSchema>

/** GET /api/businesses/{businessId}/events/{eventId}/registrations */
export const eventRegistrationsResponseSchema = z.object({
  questions: z.array(registrationQuestionSchema),
  counts: registrationCountsSchema,
  registrations: z.array(eventRegistrationSchema),
})
export type EventRegistrationsResponse = z.infer<typeof eventRegistrationsResponseSchema>

export const defaultRegistrationConfig: EventRegistrationConfig = {
  enabled: false,
  questions: [],
  seatLimit: null,
  waitlist: true,
  deadline: null,
}

/** Where sign-ups stand: closed after the deadline or the event's end, else by the seats taken. */
export function registrationStateOf(
  config: Pick<EventRegistrationConfig, 'seatLimit' | 'waitlist' | 'deadline'>,
  confirmed: number,
  closesAt: Date,
  now: Date = new Date(),
): RegistrationState {
  const deadline = config.deadline ? new Date(config.deadline) : null
  if (now >= closesAt || (deadline && now >= deadline)) return 'closed'
  if (config.seatLimit === null || confirmed < config.seatLimit) return 'open'
  return config.waitlist ? 'waitlist' : 'full'
}

export type RegistrationAnswerIssue = 'required' | 'invalid' | 'too_long' | 'unknown'

/**
 * Checks answers against the form: keeps only known questions, trims text, drops empty optional
 * answers. Returns the cleaned answers or the issue per question id.
 */
export function validateRegistrationAnswers(
  questions: readonly RegistrationQuestion[],
  answers: Record<string, unknown>,
):
  | { ok: true; answers: RegistrationAnswers }
  | { ok: false; issues: Record<string, RegistrationAnswerIssue> } {
  const issues: Record<string, RegistrationAnswerIssue> = {}
  const cleaned: RegistrationAnswers = {}
  const known = new Set(questions.map((question) => question.id))
  for (const key of Object.keys(answers)) {
    if (!known.has(key)) issues[key] = 'unknown'
  }
  for (const question of questions) {
    const result = cleanAnswer(question, answers[question.id])
    if (result.kind === 'issue') issues[question.id] = result.issue
    else if (result.kind === 'value') cleaned[question.id] = result.value
    else if (question.required) issues[question.id] = 'required'
  }
  return Object.keys(issues).length > 0 ? { ok: false, issues } : { ok: true, answers: cleaned }
}

type CleanResult =
  | { kind: 'empty' }
  | { kind: 'value'; value: RegistrationAnswerValue }
  | { kind: 'issue'; issue: RegistrationAnswerIssue }

function cleanAnswer(question: RegistrationQuestion, raw: unknown): CleanResult {
  if (raw === undefined || raw === null) return { kind: 'empty' }
  const options = question.options ?? []
  switch (question.type) {
    case 'short_text':
    case 'long_text': {
      if (typeof raw !== 'string') return { kind: 'issue', issue: 'invalid' }
      const text = raw.trim()
      if (!text) return { kind: 'empty' }
      const max =
        question.type === 'short_text' ? REGISTRATION_SHORT_TEXT_MAX : REGISTRATION_LONG_TEXT_MAX
      if (text.length > max) return { kind: 'issue', issue: 'too_long' }
      return { kind: 'value', value: text }
    }
    case 'single_choice': {
      if (typeof raw !== 'string') return { kind: 'issue', issue: 'invalid' }
      if (!raw) return { kind: 'empty' }
      return options.includes(raw)
        ? { kind: 'value', value: raw }
        : { kind: 'issue', issue: 'invalid' }
    }
    case 'multiple_choice': {
      if (!Array.isArray(raw) || raw.some((item) => typeof item !== 'string')) {
        return { kind: 'issue', issue: 'invalid' }
      }
      const picked = [...new Set(raw as string[])]
      if (picked.some((item) => !options.includes(item))) {
        return { kind: 'issue', issue: 'invalid' }
      }
      if (picked.length === 0) return { kind: 'empty' }
      return { kind: 'value', value: options.filter((option) => picked.includes(option)) }
    }
    case 'checkbox': {
      if (typeof raw !== 'boolean') return { kind: 'issue', issue: 'invalid' }
      if (!raw) return question.required ? { kind: 'empty' } : { kind: 'value', value: false }
      return { kind: 'value', value: true }
    }
    case 'number': {
      if (typeof raw === 'string' && raw.trim() === '') return { kind: 'empty' }
      const value = typeof raw === 'string' ? Number(raw.trim().replace(',', '.')) : raw
      if (typeof value !== 'number' || !Number.isFinite(value)) {
        return { kind: 'issue', issue: 'invalid' }
      }
      return { kind: 'value', value }
    }
  }
}
